import { Injectable, inject } from '@angular/core';
import { Observable, from, map, catchError, throwError } from 'rxjs';
import {
    createActorWebClient,
    ServiceClientError,
    type ActorWebClient,
} from '@workgraph/actor-service-web-client';
import { environment } from '../../environments/environment';
import { AuthService } from '../services/auth.service';
import type { Role, CreateRoleRequest, UpdateRoleRequest } from '../models/role.model';

/** How the caught error was classified — components branch on this, never on message text. */
export type RoleErrorKind = 'not-found' | 'generic';

/**
 * A renderable, typed error the components can branch on by CODE rather than message text.
 *
 * `.message` is always the user-facing copy (components render `err.message`); `.kind` and
 * `.status` are the reliable discriminators derived from the client's `statusCode`.
 */
export class RoleError extends Error {
    constructor(
        message: string,
        readonly kind: RoleErrorKind,
        readonly status?: number,
    ) {
        super(message);
        this.name = 'RoleError';
    }
}

/**
 * Thin Angular seam between DI and the generated actor-service web-client for Roles.
 *
 * It owns exactly four concerns: (1) constructing the web-client with the api-gateway host
 * and the Cognito bearer token, (2) bridging the client's promises to RxJS Observables,
 * (3) unwrapping the `{data}` / `{data,count}` response envelopes, and (4) mapping errors to
 * friendly, renderable, TYPED `RoleError`s.
 *
 * Unlike AgentGateway there is NO `runtime-unavailable` case — a Role has no runtime, so the
 * only server-validated failure worth discriminating is a 404 (not-found). Detection derives
 * PURELY from `statusCode` (reliable), never from message text (unreliable — `statusText` is
 * a bare reason phrase on 1.0.0 and EMPTY over HTTP/2 in prod): any 404 → not-found;
 * everything else → a per-operation fallback. A meaningful, non-generic `.message` (present
 * only once the client reads the `{error}` body) is surfaced as a bonus, never depended upon.
 */
@Injectable({ providedIn: 'root' })
export class RoleGateway {
    private readonly auth = inject(AuthService);

    private readonly client: ActorWebClient = createActorWebClient({
        baseUrl: environment.gatewayBaseUrl,
        getToken: () => this.auth.getIdToken(),
    });

    listRoles(projectId: string): Observable<Role[]> {
        return from(this.client.listRoles({ path: { projectId } })).pipe(
            map((result) => result.data?.data ?? []),
            catchError((err) => this.fail(err, 'We could not load the roles. Please try again.')),
        );
    }

    getRole(projectId: string, roleId: string): Observable<Role> {
        return from(this.client.getRole({ path: { projectId, roleId } })).pipe(
            map((result) => this.requireData(result.data?.data, 'role')),
            catchError((err) => this.fail(err, 'We could not load this role. Please try again.')),
        );
    }

    createRole(projectId: string, body: CreateRoleRequest): Observable<Role> {
        return from(this.client.createRole({ path: { projectId }, body })).pipe(
            map((result) => this.requireData(result.data?.data, 'role')),
            catchError((err) => this.fail(err, 'We could not create the role. Please try again.')),
        );
    }

    updateRole(projectId: string, roleId: string, body: UpdateRoleRequest): Observable<Role> {
        return from(this.client.updateRole({ path: { projectId, roleId }, body })).pipe(
            map((result) => this.requireData(result.data?.data, 'role')),
            catchError((err) => this.fail(err, 'We could not update the role. Please try again.')),
        );
    }

    deleteRole(projectId: string, roleId: string): Observable<void> {
        return from(this.client.deleteRole({ path: { projectId, roleId } })).pipe(
            map(() => void 0),
            catchError((err) => this.fail(err, 'We could not delete the role. Please try again.')),
        );
    }

    /** Guard against a 2xx with an unexpectedly empty envelope. */
    private requireData<T>(data: T | undefined | null, what: string): T {
        if (data === undefined || data === null) {
            throw new Error(`The server returned no ${what} data.`);
        }
        return data;
    }

    /** Map a thrown client error to a friendly, renderable, TYPED `RoleError`. */
    private fail(err: unknown, fallback: string): Observable<never> {
        return throwError(() => this.toRoleError(err, fallback));
    }

    /**
     * Classify the caught error by `statusCode` (reliable) — never by message text.
     * - any 404 → not-found.
     * - everything else → the per-operation fallback (or a specific non-generic message if present).
     */
    private toRoleError(err: unknown, fallback: string): RoleError {
        if (err instanceof ServiceClientError) {
            const status = err.statusCode;
            const specific = this.meaningfulMessage(err.message);

            if (status === 404) {
                return new RoleError(specific ?? 'This role was not found.', 'not-found', 404);
            }
            return new RoleError(specific ?? fallback, 'generic', status);
        }
        if (err instanceof Error && err.message) {
            return new RoleError(err.message, 'generic');
        }
        return new RoleError(fallback, 'generic');
    }

    /**
     * Return the error's own message only when it carries something meaningful — i.e. non-empty
     * AND not a bare HTTP reason phrase. Returns `undefined` otherwise so callers fall back.
     */
    private meaningfulMessage(raw: string | undefined | null): string | undefined {
        const message = (raw ?? '').trim();
        if (!message || this.isGenericHttpMessage(message)) {
            return undefined;
        }
        return message;
    }

    private isGenericHttpMessage(message: string): boolean {
        const generic = [
            'bad request',
            'not found',
            'internal server error',
            'forbidden',
            'unauthorized',
        ];
        const normalized = message.toLowerCase();
        return generic.some(
            (g) => normalized === g || normalized === `request failed with status ${g}`,
        );
    }
}
